import DOMPurify from 'dompurify';
import { DIAGRAM_TOKEN_NAMES } from '@kentik/ui-shared/kaizen/diagramTokens';

/**
 * DOMPurify configuration for sanitizing untrusted SVG markup (e.g. AI-generated SVG).
 *
 * SVG can natively carry executable content (`<script>`, `on*` handlers, `javascript:` URIs,
 * HTML via `<foreignObject>`, SMIL animation that rewrites other attributes at runtime), so
 * rendering it inline via `dangerouslySetInnerHTML` requires sanitizing first. This config is
 * locked down for static, non-interactive, self-contained SVG.
 */

// A dedicated instance (rather than the shared default `DOMPurify` singleton also used by
// TextPanel/MermaidDiagram/notification-template sanitization) so the hook below only ever
// runs for SVG sanitization and can't change behavior for unrelated HTML sanitizers.
const svgPurifier = DOMPurify(window);

// Same-document fragment or an inline raster image; anything else is an external reference
// (SSRF / tracking-pixel / exfiltration risk when the browser fetches it on render).
const LOCAL_FRAGMENT_REGEXP = /^#[A-Za-z0-9_][A-Za-z0-9_.:-]*$/;
const INLINE_RASTER_IMAGE_REGEXP =
  /^data:image\/(?:png|jpe?g|gif|webp)(?:;[\w-]+=[\w.-]+)*;base64,[A-Za-z0-9+/]*={0,2}$/i;

// Must mirror DOMPurify's own (hardcoded, non-configurable) SANITIZE_NAMED_PROPS prefix so
// fragment references below still resolve after their target `id` gets renamed.
const NAMED_PROP_PREFIX = 'user-content-';
const SVG_DIAGRAM_SCOPE_CONFIG_KEY = 'svgDiagramScope';
const SVG_DIAGRAM_SCOPE_REGEXP = /^[A-Za-z0-9_-]+$/;
type SvgPurifyConfig = DOMPurify.Config & { [SVG_DIAGRAM_SCOPE_CONFIG_KEY]?: string };

// Attributes that hold a direct URI reference.
const URI_REF_ATTRS = ['href', 'xlink:href', 'src'];
// Presentation attributes that can hold a `url(...)` reference (e.g. `fill="url(#gradient)"`).
const URL_FN_REF_ATTRS = ['fill', 'stroke', 'filter', 'mask', 'clip-path', 'marker-start', 'marker-mid', 'marker-end'];
const LOCAL_URL_FN_REGEXP =
  /url\(\s*(?:(['"])(#[A-Za-z0-9_][A-Za-z0-9_.:-]*)\1|(#[A-Za-z0-9_][A-Za-z0-9_.:-]*))\s*\)/gi;
const URL_FN_START_REGEXP = /url\s*\(/i;

function hasUnsafeReferenceSyntax(value: string): boolean {
  return (
    value.includes('\\') ||
    value.includes('/*') ||
    Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f);
    })
  );
}

// Returns the value rewritten to match a renamed `id` when it's a same-document fragment,
// unchanged when it's an allowed data: image, or null when it should be dropped.
function getScopedFragment(fragment: string, scope: string | undefined): string {
  return `#${NAMED_PROP_PREFIX}${scope ? `${scope}-` : ''}${fragment.slice(1)}`;
}

function resolveAllowedUri(value: string, scope: string | undefined): string | null {
  if (hasUnsafeReferenceSyntax(value)) {
    return null;
  }
  if (LOCAL_FRAGMENT_REGEXP.test(value)) {
    return getScopedFragment(value, scope);
  }
  return INLINE_RASTER_IMAGE_REGEXP.test(value) ? value : null;
}

// CSS escapes and comments can make text that does not contain a literal `url(` parse as one.
// Reject those constructs, then accept and rewrite only strict same-document URL functions.
function rewriteLocalUrlReferences(value: string, scope: string | undefined): string | null {
  if (hasUnsafeReferenceSyntax(value)) {
    return null;
  }

  const withoutAllowedReferences = value.replace(LOCAL_URL_FN_REGEXP, '');
  if (URL_FN_START_REGEXP.test(withoutAllowedReferences)) {
    return null;
  }

  return value.replace(LOCAL_URL_FN_REGEXP, (_match, quote, quotedFragment, unquotedFragment) => {
    const fragment = quotedFragment ?? unquotedFragment;
    const resolved = getScopedFragment(fragment, scope);
    return quote ? `url(${quote}${resolved}${quote})` : `url(${resolved})`;
  });
}

// NOTE: DOMPurify's `ALLOWED_URI_REGEXP` config option is tested against the value of *every*
// attribute (not just URI-bearing ones, e.g. `fill="red"` gets checked too), so tightening it
// globally corrupts ordinary geometry/style attributes instead of just locking down references.
// We leave DOMPurify's own (lenient) URI handling in place and, in a hook, explicitly re-check
// only the attributes that can actually carry a same-document or external reference.
svgPurifier.addHook('afterSanitizeAttributes', (node, _hookEvent, config) => {
  const scope = (config as SvgPurifyConfig)[SVG_DIAGRAM_SCOPE_CONFIG_KEY];
  const id = node.getAttribute('id');
  if (scope && id?.startsWith(NAMED_PROP_PREFIX)) {
    node.setAttribute('id', `${NAMED_PROP_PREFIX}${scope}-${id.slice(NAMED_PROP_PREFIX.length)}`);
  }

  URI_REF_ATTRS.forEach((attr) => {
    const value = node.getAttribute(attr);
    if (value === null) {
      return;
    }

    const resolved = resolveAllowedUri(value, scope);
    if (resolved === null) {
      node.removeAttribute(attr);
    } else if (resolved !== value) {
      node.setAttribute(attr, resolved);
    }
  });

  URL_FN_REF_ATTRS.forEach((attr) => {
    const value = node.getAttribute(attr);
    if (value === null) {
      return;
    }

    const rewritten = rewriteLocalUrlReferences(value, scope);
    if (rewritten === null) {
      node.removeAttribute(attr);
    } else if (rewritten !== value) {
      node.setAttribute(attr, rewritten);
    }
  });
});

// An unknown custom property makes the declaration invalid at computed-value time, so the
// property falls back to its CSS initial value — black for `fill`, i.e. invisible in dark mode.
// Rewriting hallucinated token names to their author fallback keeps that failure silent-but-safe.
const PAINT_ATTRS = new Set(['fill', 'stroke', 'stop-color', 'flood-color', 'lighting-color', 'color']);
const KNOWN_TOKEN_NAMES = new Set<string>(DIAGRAM_TOKEN_NAMES);

// Index of the ')' matching the '(' at `open`, or -1 when unbalanced.
function findMatchingParen(value: string, open: number): number {
  let depth = 0;
  for (let i = open; i < value.length; i += 1) {
    if (value[i] === '(') {
      depth += 1;
    } else if (value[i] === ')') {
      depth -= 1;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
}

// Index of the first comma not nested inside parens, or -1.
function findTopLevelComma(value: string): number {
  let depth = 0;
  for (let i = 0; i < value.length; i += 1) {
    if (value[i] === '(') {
      depth += 1;
    } else if (value[i] === ')') {
      depth -= 1;
    } else if (value[i] === ',' && depth === 0) {
      return i;
    }
  }
  return -1;
}

// Returns the value with unknown var() references replaced by their fallback, or null when
// the reference has no usable fallback and the attribute should be dropped instead.
function rewriteVarReferences(value: string, unknownFallback: string | null): string | null {
  const start = value.indexOf('var(');
  if (start === -1) {
    return value;
  }

  const end = findMatchingParen(value, start + 3);
  if (end === -1) {
    return value;
  }

  const inner = value.slice(start + 4, end);
  const comma = findTopLevelComma(inner);
  const name = (comma === -1 ? inner : inner.slice(0, comma)).trim();
  const rawFallback = comma === -1 ? null : inner.slice(comma + 1).trim();

  const fallback = rawFallback === null ? null : rewriteVarReferences(rawFallback, unknownFallback);
  if (rawFallback !== null && fallback === null) {
    return null;
  }

  let replacement: string | null;
  if (KNOWN_TOKEN_NAMES.has(name)) {
    replacement = fallback === null ? `var(${name})` : `var(${name}, ${fallback})`;
  } else {
    replacement = fallback !== null && fallback !== '' ? fallback : unknownFallback;
  }
  if (replacement === null) {
    return null;
  }

  const rest = rewriteVarReferences(value.slice(end + 1), unknownFallback);
  if (rest === null) {
    return null;
  }

  return value.slice(0, start) + replacement + rest;
}

svgPurifier.addHook('afterSanitizeAttributes', (node) => {
  Array.from(node.attributes).forEach(({ name, value }) => {
    if (!value.includes('var(')) {
      return;
    }

    const rewritten = rewriteVarReferences(value, PAINT_ATTRS.has(name) ? 'currentColor' : null);
    if (rewritten === null) {
      node.removeAttribute(name);
    } else if (rewritten !== value) {
      node.setAttribute(name, rewritten);
    }
  });
});

export const SVG_DOMPURIFY_CONFIG: DOMPurify.Config = {
  // Whitelist only SVG; the HTML profile is omitted so arbitrary HTML cannot be smuggled in.
  USE_PROFILES: { svg: true, svgFilters: true },

  FORBID_TAGS: [
    'script',
    'foreignObject', // can embed a full HTML subtree
    // SMIL animation can mutate other attributes (incl. href/on*) after sanitization.
    'animate',
    'animateTransform',
    'animateMotion',
    'set',
    'mpath',
    // Allows global CSS, @import of remote stylesheets, and attribute-selector exfiltration.
    'style'
  ],

  // DOMPurify already strips every `on*` handler; this list is defense-in-depth, not the
  // mechanism, and is not exhaustive. `style` is forbidden as an inline CSS exfiltration channel.
  FORBID_ATTR: [
    'onload',
    'onerror',
    'onclick',
    'onmouseover',
    'onmouseenter',
    'onmousemove',
    'onmouseout',
    'onfocus',
    'onbegin',
    'onend',
    'onrepeat',
    // Can load an external cursor image and is unnecessary for static diagrams.
    'cursor',
    'style'
  ],

  ALLOW_DATA_ATTR: false,

  // Prevent DOM clobbering: prefixes id/name so injected SVG can't shadow document/form props.
  // (References to renamed ids are fixed up by the afterSanitizeAttributes hook above.)
  SANITIZE_NAMED_PROPS: true,

  // Drop the contents of forbidden elements wholesale rather than reparenting them.
  KEEP_CONTENT: false
};

/**
 * Sanitize an untrusted SVG string, stripping any script / event-handler / foreignObject /
 * animation / external-resource content. Returns an empty string when the input is missing or
 * has no `<svg>` root after sanitization, so callers can render an error state.
 */
export function sanitizeSvg(svg: string | null | undefined, scope?: string): string {
  if (!svg || typeof svg !== 'string') {
    return '';
  }

  const clean = String(
    svgPurifier.sanitize(svg, {
      ...SVG_DOMPURIFY_CONFIG,
      [SVG_DIAGRAM_SCOPE_CONFIG_KEY]: scope && SVG_DIAGRAM_SCOPE_REGEXP.test(scope) ? scope : undefined
    } as SvgPurifyConfig)
  );

  // Require the output to begin with an <svg> element so a stray fragment can't masquerade
  // as a valid root.
  if (!/^\s*<svg[\s>]/i.test(clean)) {
    return '';
  }

  return clean;
}
